import { useLocation } from "react-router-dom";
import { trackEvent, buildWhatsAppUrl, trackGoogleAdsWhatsAppConversion } from "@/lib/tracking";
import { trackConversion } from "@/lib/analyticsTracker";
import { buildWhatsAppUrlRio } from "@/data/bairrosRio";
import { buildWhatsAppUrlGoi } from "@/data/bairrosGoianesia";
import { useCity } from "@/contexts/CityContext";

/**
 * Hook that provides tracked click handlers for WhatsApp and Phone CTAs.
 * Ensures conversion events are sent before navigation occurs.
 */
export function useTrackedCTA() {
  const location = useLocation();
  const { city, isRio, phone } = useCity();

  const whatsAppUrl = isRio
    ? buildWhatsAppUrlRio()
    : city === "goianesia"
      ? buildWhatsAppUrlGoi()
      : buildWhatsAppUrl();

  /**
   * Returns an onClick handler for WhatsApp CTAs.
   * Pass an optional `customUrl` to override the default city WhatsApp URL
   * (e.g. for Beneficios page with custom message text).
   */
  const handleWhatsAppClick = (source: string, customUrl?: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    trackEvent({
      event: "click_cta_primary",
      page_path: location.pathname,
      page_title: document.title,
      cta_location: source,
    });
    trackConversion("whatsapp_click", { source });
    trackGoogleAdsWhatsAppConversion(isRio);
    const targetUrl = customUrl || whatsAppUrl;
    setTimeout(() => {
      window.open(targetUrl, "_blank", "noopener,noreferrer");
    }, 600);
  };

  const handlePhoneClick = (source: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    trackEvent({
      event: "click_cta_secondary",
      page_path: location.pathname,
      page_title: document.title,
      cta_location: source,
    });
    trackConversion("phone_click", { source });
    setTimeout(() => {
      window.location.href = phone;
    }, 600);
  };

  return { whatsAppUrl, phone, handleWhatsAppClick, handlePhoneClick };
}
